/**
 * i18next setup, and the one decision that governs it: WHO gets a language.
 *
 * ── WHY NOT BROWSER DETECTION ───────────────────────────────────────────────
 *
 * The obvious wiring is `order: ["navigator", ...]`, and it is wrong here. Only
 * a minority of this app's components call `useTranslation`; everything else is
 * hardcoded English. So resolving a French browser to `fr` would render the few
 * translated screens in French INSIDE an otherwise English app — visibly worse
 * than English everywhere, and it would land on every existing user at once
 * without anyone asking for it.
 *
 * So detection is opt-IN and nothing else:
 *
 *   order: ["querystring", "localStorage"]
 *
 * A user who has never passed `?lng=` sees exactly what they saw before this
 * file changed. `?lng=fr` switches, and `caches: ["localStorage"]` persists the
 * choice so a reload — or any later navigation that drops the query string —
 * holds it. `navigator` is deliberately absent from the order, not merely
 * unlisted: adding it is the change that silently translates the whole user
 * base, so it belongs in a decision, never in a tidy-up.
 *
 * ── WHY AN UNKNOWN VALUE CANNOT BREAK THE UI ────────────────────────────────
 *
 * `localStorage` is a read boundary, and a read boundary must degrade to a
 * usable value of the right type rather than to nothing. `supportedLngs` is
 * what enforces that: a stored value that is not a language we ship — stale,
 * hand-edited, or corrupt — is filtered out and `fallbackLng` applies, so the
 * worst case is English rather than a blank screen or a raw key path.
 *
 * A REGIONAL tag is handled by that same list: `?lng=fr-BE` resolves to `fr`
 * rather than falling through to English. Which matters per KEY, not per
 * locale — a locale that ships no copy for a given namespace still falls back
 * to English there, and that is the normal state for most of this app.
 *
 * `load: "languageOnly"` is deliberately NOT set, and its name is a trap here.
 * Measured both ways on this config: WITHOUT it `language` reads `fr`, and
 * WITH it `language` reads `fr-BE`. It is `supportedLngs` that normalises the
 * tag, so adding `load` would UNDO the flattening rather than cause it.
 *
 * `fr` is the value we want, because `language` is cached to localStorage and
 * is passed to `Intl` by the Roles ledger, and both are specified against the
 * set of languages this app actually ships.
 */

import i18n from "i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import { initReactI18next } from "react-i18next"

import { STORAGE_KEYS } from "@/common/services/localStorage.keys"

import de from "./locales/de/default.json"
import en from "./locales/en/default.json"
import es from "./locales/es/default.json"
import fr from "./locales/fr/default.json"
import ja from "./locales/ja/default.json"
import nl from "./locales/nl/default.json"

/**
 * Every language this app ships, and the single source of truth for it.
 *
 * `supportedLngs` is derived from these keys rather than written out a second
 * time, so adding a locale here cannot leave behind an allowlist that silently
 * rejects it.
 */
const RESOURCES = {
  en: { translation: en },
  fr: { translation: fr },
  es: { translation: es },
  de: { translation: de },
  ja: { translation: ja },
  nl: { translation: nl }
} as const

export const SUPPORTED_LANGUAGES = Object.keys(RESOURCES)

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: RESOURCES,
    // No `lng` — it would override detection entirely and pin every session to
    // one language. `fallbackLng` is what makes English the default when
    // nothing is asked for and nothing is stored.
    fallbackLng: "en",
    supportedLngs: SUPPORTED_LANGUAGES,
    detection: {
      order: ["querystring", "localStorage"],
      lookupQuerystring: "lng",
      lookupLocalStorage: STORAGE_KEYS.LANGUAGE,
      caches: ["localStorage"]
    },
    interpolation: {
      escapeValue: false
    }
  })

export default i18n
